'use client';

import { isEqual } from 'es-toolkit';
import { getCookie, getStorage } from 'minimal-shared/utils';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useCookies, useLocalStorage } from 'minimal-shared/hooks';

import { SettingsContext } from './settings-context';
import { SETTINGS_STORAGE_KEY } from '../settings-config';
import { useTranslation } from 'react-i18next';
import { usePathname, useRouter } from 'next/navigation';
import { CONFIG, ENV } from 'global-config.mjs';

// ----------------------------------------------------------------------

export function SettingsProvider({
  children,
  cookieSettings,
  defaultSettings,
  storageKey = SETTINGS_STORAGE_KEY,
}) {
  const isCookieEnabled = !!cookieSettings;
  const useStorage = isCookieEnabled ? useCookies : useLocalStorage;
  const initialSettings = isCookieEnabled ? cookieSettings : defaultSettings;
  const getStorageValue = isCookieEnabled ? getCookie : getStorage;
  const { i18n } = useTranslation();

  // ✅ Step 1: Read stored settings synchronously before render
  const storedValue = typeof window !== 'undefined' ? getStorageValue(storageKey) : null;

  // ✅ Step 2: Merge stored values with defaults
  const initialLocale = storedValue?.locale || defaultSettings.locale;
  const initialDirection = storedValue?.direction || defaultSettings.direction;

  // ✅ Step 3: Set the <html> tag’s direction + lang immediately (for SEO + no flicker)
  if (typeof document !== 'undefined') {
    document.documentElement.lang = initialLocale;
    document.documentElement.dir = initialDirection;
  }

  // ✅ Step 4: Initialize storage hook
  const { state, setState, resetState, setField } = useStorage(storageKey, {
    ...initialSettings,
    locale: initialLocale,
    direction: initialDirection,
  });

  const [openDrawer, setOpenDrawer] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const onToggleDrawer = useCallback(() => setOpenDrawer((prev) => !prev), []);
  const onCloseDrawer = useCallback(() => setOpenDrawer(false), []);

  const canReset = !isEqual(state, defaultSettings);

  const currentConfig = CONFIG[ENV];

  const onReset = useCallback(() => {
    resetState(defaultSettings);

    const newLocale = defaultSettings?.locale || 'ar';
    localStorage.setItem('i18nextLng', newLocale);
    i18n.changeLanguage(newLocale);

    // ✅ Base path dynamically from config
    const basePath = currentConfig.BASE_PATH || '';
    const current = window.location.pathname;

    // ✅ Safely replace locale only after base path
    const regex = new RegExp(`^(${basePath})(?:/(en|ar))?`);
    const newUrl = current.replace(regex, `$1/${newLocale}`);

    // ✅ Redirect with reload to apply dir/locale cleanly
    window.location.href = newUrl;
  }, [defaultSettings, resetState, i18n, currentConfig]);

  // const onReset = useCallback(() => {
  //   resetState(defaultSettings);
  //   localStorage.setItem('i18nextLng', defaultSettings.locale);
  // }, [defaultSettings, resetState]);

  // Version check and reset handling
  useEffect(() => {
    const storedValue = getStorageValue(storageKey);

    if (storedValue) {
      try {
        if (!storedValue.version || storedValue.version !== defaultSettings.version) {
          onReset();
        }
      } catch {
        onReset();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Wait for localStorage/cookie to hydrate
    setHydrated(true);
  }, []);

  // ✅ Step 5: Reactively update HTML dir/lang when state changes
  useEffect(() => {
    if (state.locale) {
      document.documentElement.lang = state.locale;
      document.documentElement.dir = state.locale === 'ar' ? 'rtl' : 'ltr';
    }
  }, [state.locale]);

  const memoizedValue = useMemo(
    () => ({
      canReset,
      onReset,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
      state,
      setState,
      setField,
    }),
    [canReset, onReset, openDrawer, onCloseDrawer, onToggleDrawer, state, setField, setState]
  );

  if (!hydrated) {
    // Optionally, show a spinner or nothing while hydrating
    return null;
  }

  return <SettingsContext value={memoizedValue}>{children}</SettingsContext>;
}
